import { Component, OnChanges, Output, Input, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { formatDate, NgClass, NgTemplateOutlet } from '@angular/common';

import { ExcelService, rowType, styleType } from "app/services/excel.service";
import { NgbTooltip, NgbPagination } from '@ng-bootstrap/ng-bootstrap';
export { styleType } from "app/services/excel.service";

let NWMTableID = 0;

export type optionsColumnType = {
  class?: string;
  dateFormat?: string;
  field: string;
  notSortable?: boolean;
  numberFormat?: number;
  title: string;
};

export type optionsType = {
  tableSubscriptor: (callback: (data: dataType) => void, error: () => void) => void;
  exportSubscriptor: (callback: (data: dataType) => void, error: () => void) => void;
  columns: optionsColumnType[];
  request: {
    FilterOperator: string[];
    FilterProperty: string[];
    Filtertype: string[];
    FilterValue: (string | number)[];
    Page: number;
    PageSize: number;
    SortDirections: string[];
    SortFields: string[];
  };
  tooltipField: string;
};

type itemType = Record<string, null | number | string>;

export type dataType = {
  TotalItems: number;
  Items: itemType[];
};

@Component({
    selector: 'NWMTable',
    templateUrl: './NWMTable.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./NWMTable.component.scss'],
    imports: [NgClass, NgTemplateOutlet, NgbTooltip, NgbPagination]
})
export class NWMTableComponent implements OnChanges {
  private excel = inject(ExcelService);
  private cdr = inject(ChangeDetectorRef);

  @Input() options?: optionsType;

  @Output() tblSort: EventEmitter<[string, string]> = new EventEmitter();
  @Output() tblRowClick: EventEmitter<{ item: itemType, index: number }> = new EventEmitter();

  public sortBy?: string;
  public asc = true;
  public data?: dataType;
  public maxPage = 10;
  public NWMTableID = 0;
  public formattedData: (null | string | number)[][] = [];
  public showLoader = false;

  constructor() {
    this.NWMTableID = ++NWMTableID;
  }

  ngOnChanges() {
    this.sortBy = this.options?.request.SortFields ? this.options.request.SortFields[0] : '';
    this.asc = this.options?.request.SortDirections && this.options.request.SortDirections[0] == 'Ascending' || false;
    this.getData();
  }

  formatValue(item: itemType, col: optionsColumnType): null | string | number {
    const val = item[col.field];
    if (col.numberFormat !== undefined) return val === 0 ? '-' : (val as number).toFixed(col.numberFormat).replace(/\B(?=(\d{3})+(?!\d))/g, ",").minusSignToParens();
    if (col.dateFormat == 'mediumDate') return formatDate(val as string, 'MMM d, yyyy', 'en-US');
    return val;
  }

  sort(col: optionsColumnType) {
    if (col.notSortable) return;
    const sortBy = col.field;
    this.asc = sortBy != this.sortBy || !this.asc;
    this.sortBy = sortBy;
    this.tblSort.emit([this.sortBy, this.asc ? 'asc' : 'desc']);

    this.options!.request.SortFields = [this.sortBy as string];
    this.options!.request.SortDirections = [this.asc ? 'Ascending' : 'Descending'];
    this.getData();
  }

  rowClick(item: itemType, index: number) {
    this.tblRowClick.emit({ item, index });
  }

  getData() {
    this.showLoader = true;

    this.options?.tableSubscriptor(
      data => {
        this.data = data;
        this.maxPage = Math.ceil(data['TotalItems'] / this.options!.request.PageSize);
        this.formatData();
        setTimeout(()=>{this.cdr.detectChanges();});
        this.showLoader = false;
      },
      () => {
        this.showLoader = false;
      },
    );
  }

  formatData() {
    this.formattedData = [];
    this.data!.Items.forEach(item => {
      const formattedRow: (null | string | number)[] = [];
      this.options!.columns.forEach(c => {
        formattedRow.push(this.formatValue(item , c));
      });
      this.formattedData.push(formattedRow);
    });
  }

  gotoPage(page: number) {
    this.options!.request.Page = page;
    this.getData();
  }

  asString(value: unknown): string {
    return value as string;
  }

  export(fileName = 'export.xlsx', sheetTitle = 'Sheet 1', rows: rowType[] = [], styles: styleType[] = []) {
    if (this.data!.Items.length == 0) return;

    this.showLoader = true;

    this.options?.exportSubscriptor(
      data => {
        setTimeout(() => {
          if (data.Items.length>0) {
            const headers: string[] = [];
            this.options!.columns.forEach(c=>{
              headers.push(c.title);
              if (c.numberFormat !== undefined || c.dateFormat !== undefined) {
                styles.push({col:headers.length-1, align: 'R C'});
                if (c.numberFormat !== undefined)
                  styles.push({row: (rows.length+2) + '-' + (data.Items.length+rows.length+2),col:headers.length-1, format: '#,##0.'+(c.numberFormat == 3 ? '000' : '00')+';(#,##0.'+(c.numberFormat == 3 ? '000' : '00')+');&quot;-&quot;'});
              }
            });

            const lastCol = headers.length-1;

            rows.push([], headers);
            rows[0][lastCol] = this.excel.toExcelLocalTime(new Date());
            styles.push(
              // styles can be applied to row, col or cell, but if you do it more then once, only the latest will apply
              {col:'0-'+lastCol, width:20},
              {row:rows.length-1, height:30},
              {row:0, col:lastCol, format: "m-d-yyyy h:mm:ss AM/PM", font:'8'},
              {row:rows.length-1, col:'0-'+lastCol, fill: "#183750", font: "Arial 10 #ffffff B", wrapText:1},
            );

            data.Items.forEach(item => {
              const row: (null | string | number)[] = [];
              this.options!.columns.forEach(c=>{
                if (c.numberFormat !== undefined) {
                  row.push(item[c.field]);
                } else {
                  row.push(this.formatValue(item ,c))
                }
              });
              rows.push(row);
            });
            this.excel.generate(fileName, [{sheetTitle:sheetTitle, rows:rows, styles:styles}]);
          }
          this.showLoader = false;
        });
      },
      () => {
        this.showLoader = false;
      },
    );
  }
}
